import {addLineItems, getCart} from '@/cart/cart-store';
import {endDrawer} from '@/components/drawers';
import {getLocalizedStrings} from '@/localization/localization-store';
import {createShopifyImageUrl} from '@/utilities/create-shopify-image-url';
import formatCurrency from '@/utilities/format-currency';
import {logError} from '@/utilities/log';

/**
 * Data structure for a variant displayed in the all-variants collection grid
 */
export type CollectionVariantData = {
  id: string;
  productUrl: string;
  displayTitle: string;
  displayVarTitle: string;
  available: boolean;
  image: {
    url: string;
    alt: string;
  } | null;
  price: number;
  compareAtPrice: number | null;
  hasSubscription: boolean;
  subscriptionPrice: number | null;
  subscriptionCompareAtPrice: number | null;
  selectedSellingPlanId: string | null;
  tierDiscountedPrice: number | null;
  sellingPlanAllocations: {
    price: number;
    compareAtPrice: number;
    sellingPlan: {
      id: string;
      name: string;
      recurringDeliveries: boolean;
    };
  }[];
};

export type CollectionVariantBuyStackProps = {
  variantData: CollectionVariantData;
};

/**
 * A component that displays a single variant with image, title, price, and add to cart button.
 * Subscription prices take priority over one-time purchase prices.
 */
export default function CollectionVariantBuyStack({
  variantData,
}: CollectionVariantBuyStackProps) {
  const cart = getCart();
  const currency = cart?.currency ?? 'USD';
  const buttonLabel =
    getLocalizedStrings().collectionCtaButtonLabel ?? 'Add To Cart';
  const soldOutLabel = getLocalizedStrings().buyStackSoldOut ?? 'Sold Out';

  // Determine display price - subscription prices take priority
  const displayPrice = variantData.hasSubscription
    ? (variantData.subscriptionPrice ?? variantData.price)
    : variantData.price;

  // Determine compare at price
  // For subscriptions: use subscription compare price or fall back to OTP price
  // For OTP: use compare at price
  let comparePrice: number | null = null;
  if (variantData.hasSubscription) {
    comparePrice =
      variantData.subscriptionCompareAtPrice ?? variantData.price ?? null;
  } else if (variantData.compareAtPrice) {
    comparePrice = variantData.compareAtPrice;
  }

  let variantDisplayPrice: number | null = null;
  if (variantData.tierDiscountedPrice) {
    variantDisplayPrice = variantData.tierDiscountedPrice;
  } else {
    variantDisplayPrice = displayPrice;
  }


  // Find the selected subscription allocation (market-specific from Liquid)
  // Falls back to first recurring if selectedSellingPlanId is not set
  const subscriptionAllocation = variantData.selectedSellingPlanId
    ? variantData.sellingPlanAllocations.find(
        (a) => a.sellingPlan.id === variantData.selectedSellingPlanId
      )
    : variantData.sellingPlanAllocations.find(
        (a) => a.sellingPlan.recurringDeliveries
      );

  const hasSellingPlan = variantData.sellingPlanAllocations.length > 0;

  /**
   * Handles the click event for the add to cart button.
   * If subscription is available, add with the subscription selling plan.
   */
  const handleClick = async () => {
    if (!variantData.available) return;

    try {
      endDrawer.open();
      await addLineItems([
        {
          id: parseInt(variantData.id),
          quantity: 1,
          ...(subscriptionAllocation && {
            selling_plan: parseInt(subscriptionAllocation.sellingPlan.id),
          }),
        },
      ]);
    } catch (error) {
      logError(error);
    }
  };

  return (
    <div className="flex min-h-[531px] flex-col justify-between">
      <a href={variantData.productUrl ?? ''}>
        <div className="group relative aspect-7/10 max-h-80 w-full overflow-hidden">
          {variantData.image?.url && (
            <img
              src={createShopifyImageUrl(variantData.image.url, 440)}
              alt={variantData.image.alt ?? variantData.displayVarTitle}
              className="aspect-7/10 max-h-80 w-full object-contain transition-transform duration-300 ease-in-out hover:scale-105"
            />
          )}
        </div>
      </a>

      
      {/* Product Title */}
      <div className="mt-3">
        <a
          href={variantData.productUrl ?? ''}
          className="text-deep-ocean-blue no-underline">
          <h6 className="leading-tight">{variantData.displayTitle}</h6>
          <div className="inline-block p-0 text-lg font-bold text-black">{variantData.displayVarTitle}</div>

      {hasSellingPlan && (
        <div>
            <span
            className="mt-0 inline-flex items-center text-sm font-bold text-slate-400"
            style={{fontFamily: 'Arial, sans-serif'}}>
            Buy More and Save More!
            </span>
        </div>
      )}



        </a>
      </div>

  


      <div>
        <span className="text-lg">
          {!!comparePrice && comparePrice !== displayPrice && (
            <s className="mr-1 font-bold text-slate-400">
              {formatCurrency(comparePrice, currency)}
            </s>
          )}
          <span className={comparePrice && comparePrice !== displayPrice ? 'text-red-600' : 'text-black'}>
            {formatCurrency(variantDisplayPrice, currency)}
          </span>
        </span>
      </div>

      <div className="mt-2 flex flex-col">
        <button
          className="btn btn-square btn-sm w-full font-bold uppercase"
          disabled={!variantData.available}
          onClick={() => void handleClick()}>
          {variantData.available ? buttonLabel : soldOutLabel}
        </button>
      </div>
    </div>
  );
}
